import type { DocumentSummary } from '../types';
import { TEMPLATES, type TemplateDef } from '../templates';
import { findTheme } from '../themes';

interface Props {
  documents: DocumentSummary[];
  activeId: string | null;
  onPick: (id: string) => void;
  onNewFromTemplate: (t: TemplateDef) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

export function Sidebar({ documents, activeId, onPick, onNewFromTemplate, onDuplicate, onDelete, onRename }: Props) {
  return (
    <aside className="cb-sidebar">
      <header className="cb-sidebar-head">
        <h3>Documents</h3>
        <span className="cb-count">{documents.length}</span>
      </header>

      <details className="cb-templates" open>
        <summary>+ New from template</summary>
        <div className="cb-templates-list">
          {TEMPLATES.map((t) => (
            <button key={t.id} type="button" className="cb-template" onClick={() => onNewFromTemplate(t)}>
              <span className="cb-template-name">{t.name}</span>
              <span className="cb-template-desc">{t.description}</span>
            </button>
          ))}
        </div>
      </details>

      <div className="cb-doc-list" role="list">
        {documents.length === 0 && (
          <p className="cb-empty">No documents yet. Pick a template above to start.</p>
        )}
        {documents.map((d) => (
          <div
            key={d.id}
            role="listitem"
            className={`cb-doc-card ${d.id === activeId ? 'is-active' : ''}`}
            onClick={() => onPick(d.id)}
          >
            <div className="cb-doc-thumb">
              <ThumbPaper family={findTheme(d.theme).family.toLowerCase()} />
              <span className="cb-doc-thumb-letter">{d.name.slice(0, 1).toUpperCase()}</span>
            </div>
            <div className="cb-doc-meta">
              <input
                className="cb-doc-name"
                value={d.name}
                onChange={(e) => onRename(d.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="cb-doc-sub">
                {new Date(d.updatedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                <span className="cb-doc-theme">· {findTheme(d.theme).label}</span>
              </div>
              <div className="cb-doc-actions" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => onDuplicate(d.id)}>Duplicate</button>
                <button type="button" className="cb-danger" onClick={() => {
                  if (confirm(`Delete "${d.name}"? This cannot be undone.`)) onDelete(d.id);
                }}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

function ThumbPaper({ family }: { family: string }) {
  /* Cheap deterministic colored thumbnail keyed on the theme's family.
     Real preview comes from the "Preview" mode -- this is just a pleasant
     identifier so the user can spot which doc is which at a glance. */
  return (
    <div className="cb-thumb-paper" data-theme-thumb={family} />
  );
}
